import { useTheme } from "expo-router/react-navigation";
import {
  AlertCircle,
  ArrowUpDown,
  Eye,
  Gauge,
  GitCompareArrows,
  Layers,
  Lightbulb,
  LucideIcon,
  MessageSquareQuote,
  Package,
  PlaySquare,
  RefreshCw,
  Tag,
  Target,
} from "lucide-react-native";
import { createElement, FC, useCallback, useMemo, useState } from "react";
import { View, ViewStyle } from "react-native";

import {
  Box,
  Card,
  HStack,
  Media,
  MediaGallery,
  Pressable,
  Text,
  VStack,
} from "@/components/ui";
import { useLocalization } from "@/hooks";
import { Section as SectionType } from "@/types/resources";
import { UseCase } from "@/types/utils";

import { isCellFilled } from "./cells";
import { sectionHasError } from "./completeness";
import SectionEditor from "./SectionEditor";
import {
  getUseCaseRequirements,
  useUseCaseLabels,
} from "./useCaseRequirements";

/* ── Helpers ────────────────────────────────────────────── */

const getSectionIcon = (useCase: UseCase | null): LucideIcon => {
  switch (useCase) {
    case UseCase.PreferenceTest:
      return GitCompareArrows;
    case UseCase.AdCreative:
      return PlaySquare;
    case UseCase.Awareness:
      return Eye;
    case UseCase.ClaimTest:
      return MessageSquareQuote;
    case UseCase.MaxDiff:
      return ArrowUpDown;
    case UseCase.Monadic:
      return Lightbulb;
    case UseCase.NPS:
      return Gauge;
    case UseCase.Packaging:
      return Package;
    case UseCase.PriceElasticity:
      return Tag;
    case UseCase.TURF:
      return Target;
    default:
      return Layers;
  }
};

/* ── Component ──────────────────────────────────────────── */

interface SectionProps {
  className?: string;
  disabled?: boolean;
  noBorder?: boolean;
  onDeleted: () => void;
  onPress?: () => void;
  onUpdated: (section: SectionType) => void;
  section: SectionType;
  studyId: string;
  style?: ViewStyle;
}

/**
 * Card chrome + collapsed display for a study section. Mirrors the
 * `Question` / `*QuestionEditor` split: this component owns the icon rail,
 * the press-to-edit affordance, the read-only header (title + description
 * for plain sections, products list for use-case sections), and the
 * summary line. The full edit form lives in `SectionEditor` and is
 * mounted in place of the display body when `editing` is true.
 */
const Section: FC<SectionProps> = ({
  className,
  disabled = false,
  noBorder = false,
  onDeleted,
  onPress,
  onUpdated,
  section,
  studyId,
  style = {},
}) => {
  const theme = useTheme();
  const { i18n } = useLocalization();

  const requirements = useMemo(
    () => getUseCaseRequirements(section.use_case),
    [section.use_case]
  );
  const labels = useUseCaseLabels(section.use_case);

  /* ── Edit / dialog state ── */
  const [editing, setEditing] = useState<boolean>(false);
  const [galleryIndex, setGalleryIndex] = useState(0);
  const [galleryVisible, setGalleryVisible] = useState(false);

  const handlePress = useCallback(() => {
    if (disabled) return;
    setEditing(true);
    onPress?.();
  }, [disabled, onPress]);

  /* Section's public surface (`onUpdated`) only deals with the section —
     we drop the questions array here since this view doesn't own the
     question list. The study editor that mounts the picker subscribes to
     question changes via its own create-section flow instead. */
  const handleSaved = useCallback(
    ({ section: updated }: { section: SectionType }) => {
      onUpdated(updated);
      setEditing(false);
    },
    [onUpdated]
  );

  /* ── Display-mode summary ── */
  const isFree = section.use_case === null;
  const variables = section.config?.variables ?? [];
  /* Whether this section authors cells at all: free sections do once they
     declare variables; use-case sections only when their template requires
     cells. The item-pool use cases (Preference, Awareness, MaxDiff, TURF)
     don't, so they never surface cells or a "no cells" hint. */
  const expectsCells = isFree
    ? variables.some((v) => v.name.trim())
    : requirements.min_cells > 0;
  /* Section is "in error" when it expects cells but none are filled — drives
     both the red hint and the red left rail. */
  const missingCells = sectionHasError(section);

  const summaryParts = useMemo(() => {
    const parts: string[] = [];
    /* Rotation config is surfaced as soon as it exists — config is only
       persisted once the section declares variables, so this shows the
       configured cadence even before any cells are filled in. */
    const cells = section.config?.cells_per_respondent;
    if (typeof cells === "number" && cells >= 1) {
      parts.push(labels.summaryCells(cells));
    }
    const items = section.config?.items_per_cell;
    if (typeof items === "number" && items > 1) {
      parts.push(labels.summaryItems(items));
    }
    return parts;
  }, [labels, section.config]);

  /* ── Renders ────────────────────────────────────────────── */

  /**
   * Collapsed display: shows the title and (when present) the description
   * preview below it — the colored icon rail and the section's position in
   * the list already communicate that this is a section, so we don't repeat
   * "Section N:".
   *
   * Whenever the section carries stimuli — use case or not — we surface them
   * (filled labels + optional image thumbnails) below the title/description so
   * the collapsed card communicates the actual study content at a glance.
   * Empty product state is handled by the summary line below, which shows the
   * red "no X added" hint.
   */
  const renderDisplay = () => {
    /* One card per filled cell — a "Cell N" header over a compact list of the
       cell's variables and their values (text inline, images as thumbnails).
       Thumbnails share a single gallery, indexed in render order. */
    const namedVariables = variables.filter((v) => v.name.trim());
    const filledCells = section.cells
      .map((cell, index) => ({ cell, index }))
      .filter(({ cell }) => isCellFilled(cell, variables));
    const imageUrls: string[] = [];
    filledCells.forEach(({ cell }) =>
      namedVariables.forEach((v) => {
        if (v.type !== "image") return;
        const url = cell[`${v.name}_url`] ?? cell[v.name];
        if (url) imageUrls.push(url);
      })
    );
    let imageCursor = 0;

    return (
      <>
        <VStack className="gap-1">
          <Text
            className="text-sm font-semibold text-typography-900 mb-1"
            numberOfLines={1}
          >
            {section.title}
          </Text>
          {section.description?.trim() && (
            <Text className="text-sm text-typography-900 leading-relaxed">
              {section.description}
            </Text>
          )}
        </VStack>
        {expectsCells && filledCells.length > 0 && (
          <VStack className="gap-2 mt-3">
            <Text className="text-sm font-semibold text-typography-800">
              {isFree ? i18n.t("studies.cells.cells") : labels.stimuli}
            </Text>
            {filledCells.map(({ cell, index }) => (
              <VStack
                key={index}
                className="gap-2 rounded-xl border border-outline-100 bg-background-50 p-3"
              >
                <Text className="text-[11px] font-semibold uppercase tracking-wide text-typography-500">
                  {isFree
                    ? i18n.t("studies.cells.cellNumber", { number: index + 1 })
                    : labels.stimulusPlaceholder(index + 1)}
                </Text>
                <VStack className="gap-1.5">
                  {namedVariables.map((v, vi) => {
                    const isImage = v.type === "image";
                    const url = isImage
                      ? (cell[`${v.name}_url`] ?? cell[v.name])
                      : null;
                    const value = cell[v.name]?.trim();
                    const idx = isImage && url ? imageCursor++ : -1;
                    return (
                      <HStack key={vi} className="items-center gap-2">
                        <Text
                          className="w-24 text-xs text-typography-500"
                          numberOfLines={1}
                        >
                          {v.name}
                        </Text>
                        {isImage ? (
                          url ? (
                            <Media
                              alt={v.name}
                              height={32}
                              onPress={() => {
                                setGalleryIndex(idx);
                                setGalleryVisible(true);
                              }}
                              resizeMode="cover"
                              source={url}
                              width={32}
                            />
                          ) : (
                            <Text className="text-xs text-typography-300">
                              —
                            </Text>
                          )
                        ) : (
                          <Text
                            className="flex-1 text-xs text-typography-900"
                            numberOfLines={2}
                          >
                            {value || "—"}
                          </Text>
                        )}
                      </HStack>
                    );
                  })}
                </VStack>
              </VStack>
            ))}
          </VStack>
        )}
        {renderSummary()}
        {imageUrls.length > 0 && (
          <MediaGallery
            initialIndex={galleryIndex}
            media={imageUrls}
            onClose={() => setGalleryVisible(false)}
            visible={galleryVisible}
          />
        )}
      </>
    );
  };

  const renderSummary = () => {
    /* Surface a red "No xxx" hint when the section expects cells but has none
       yet (see `missingCells` above). */
    const missingStimuli = missingCells;
    if (!missingStimuli && summaryParts.length === 0) return null;
    return (
      <HStack className="items-center gap-1 mt-3">
        {missingStimuli && (
          <HStack className="items-center gap-1">
            <AlertCircle color="rgb(var(--color-error-500))" size={12} />
            <Text className="text-xs font-medium text-error-500">
              {labels.summaryNoStimuli}
            </Text>
          </HStack>
        )}
        {missingStimuli && summaryParts.length > 0 && (
          <Text className="text-xs text-typography-400"> · </Text>
        )}
        {summaryParts.length > 0 && (
          <>
            <RefreshCw color="rgb(var(--color-typography-400))" size={11} />
            <Text className="text-xs text-typography-400">
              {summaryParts.join(" · ")}
            </Text>
          </>
        )}
      </HStack>
    );
  };

  /* ── Card body ── */
  const cardClassName = noBorder
    ? "p-0 rounded-xl overflow-hidden"
    : `p-0 border rounded-xl overflow-hidden ${
        missingCells ? "border-error-700" : "border-outline-200"
      }`;

  // `getSectionIcon` selects one of several module-level Lucide icons. Render
  // it via `createElement` rather than assigning to a capitalized local, which
  // React flags as creating a component during render.
  const sectionIcon = getSectionIcon(section.use_case);
  const cardBody = (
    <HStack className="items-stretch">
      <View
        className={`items-center justify-start pt-4 rounded-l-xl ${
          missingCells ? "bg-error-700" : "bg-tertiary-800"
        }`}
        style={{ width: 28 }}
      >
        {createElement(sectionIcon, {
          color: "rgb(var(--color-typography-0))",
          size: 12,
        })}
      </View>
      <VStack className="flex-1 p-4">
        {editing ? (
          <SectionEditor
            disabled={disabled}
            onCancel={() => setEditing(false)}
            onDeleted={onDeleted}
            onSaved={handleSaved}
            section={section}
            studyId={studyId}
          />
        ) : (
          renderDisplay()
        )}
      </VStack>
    </HStack>
  );

  return (
    <Box
      className={`rounded-b-2xl ${className ?? ""}`}
      style={{ backgroundColor: theme.colors.background, ...style }}
    >
      {editing || disabled ? (
        <Card className={cardClassName}>{cardBody}</Card>
      ) : (
        <Pressable
          onPress={handlePress}
          style={{ alignSelf: "stretch", width: "100%" }}
        >
          <Card className={cardClassName}>{cardBody}</Card>
        </Pressable>
      )}
    </Box>
  );
};

export default Section;
